import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  parseApiDate,
  toApiDate,
  type CalendarDay,
  type CalendarEntry,
  type CalendarPayload,
  type CalendarSummary,
} from "./alertCalendarData";

/* ══════════════════════════════════════════════════════════════════════════
   Temporary stand-in for the live endpoints.

   Holds the scenario from the API guide — one tenant, one rule
   (GOVERNANCE · POLICY · 10 days before, on org BCS) and four documents, each
   version 1 on a 90-day review cycle, so every review date is its publication
   date plus 90.

   Entries are DERIVED here exactly as the service derives them, rather than
   being listed cell by cell, so any month, week or day answers correctly:

     day 0        newAlerts    the alert is raised
     day 1..N-1   reminders    the same alert restated, counting down
     day N        reviewDue    the review falls due

   Set USE_MOCK to false — or delete this file — once the API is serving.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Master switch for the recorded data. Off — the page talks to the live
 * endpoints. Flip to true to fall back to this file (demos, API downtime).
 *
 * Typed as boolean rather than the inferred literal so both branches at the
 * call site stay live for the type checker.
 */
export const USE_MOCK: boolean = false;

const LEAD_DAYS = 10;
const REVIEW_CYCLE_DAYS = 90;

const BCS = { orgId: "019fb6e4-3af5-7545-8840-117cfb166be4", orgName: "BCS" };
/* Four levels below BCS — it appears because the rule walk goes upward. */
const FINANCE = { orgId: "019fb6e5-430d-7c8d-a172-06e7c8105ef1", orgName: "Finance" };

interface MockDoc {
  documentId: string;
  title: string;
  orgId: string;
  orgName: string;
  publishedDate: string;
}

const DOCS: MockDoc[] = [
  {
    documentId: "019fc700-1111-7000-9000-000000000001",
    title: "Access Control Policy",
    ...BCS,
    publishedDate: "2026-05-06",
  },
  {
    documentId: "019fc700-1111-7000-9000-000000000002",
    title: "Acceptable Use Policy",
    ...BCS,
    publishedDate: "2026-05-08",
  },
  {
    documentId: "019fc700-1111-7000-9000-000000000003",
    title: "Information Security Policy",
    ...FINANCE,
    publishedDate: "2026-05-14",
  },
  {
    documentId: "019fc700-1111-7000-9000-000000000004",
    title: "Data Retention Policy",
    ...BCS,
    publishedDate: "2026-05-22",
  },
];

/** A document's alert window: reviewDate = published + cycle, alert = review - lead. */
interface AlertWindow {
  doc: MockDoc;
  alertDate: Date;
  reviewDate: Date;
  totalDays: number;
}

const WINDOWS: AlertWindow[] = DOCS.map((doc) => {
  const reviewDate = addDays(parseApiDate(doc.publishedDate), REVIEW_CYCLE_DAYS);
  const alertDate = addDays(reviewDate, -LEAD_DAYS);
  return {
    doc,
    alertDate,
    reviewDate,
    totalDays: differenceInCalendarDays(reviewDate, alertDate),
  };
});

function entryFor(w: AlertWindow, date: Date, dayIndex: number): CalendarEntry {
  return {
    documentId: w.doc.documentId,
    title: w.doc.title,
    version: 1,
    documentCategory: "GOVERNANCE",
    documentType: "POLICY",
    orgId: w.doc.orgId,
    orgName: w.doc.orgName,
    entryType:
      dayIndex === 0
        ? "NEW_ALERT"
        : dayIndex === w.totalDays
          ? "REVIEW_DUE"
          : "REMINDER",
    entryDate: toApiDate(date),
    dayIndex,
    totalDays: w.totalDays,
    daysRemaining: differenceInCalendarDays(w.reviewDate, date),
    alertDate: toApiDate(w.alertDate),
    reviewDate: toApiDate(w.reviewDate),
    publishedDate: w.doc.publishedDate,
    reviewCycleDays: REVIEW_CYCLE_DAYS,
    leadAmount: LEAD_DAYS,
    leadUnit: "DAYS",
    alertSummary: `${LEAD_DAYS} days before the review date`,
  };
}

const byTitle = (a: CalendarEntry, b: CalendarEntry) => a.title.localeCompare(b.title);

function buildDay(date: Date, inRange: boolean, today: Date): CalendarDay {
  const newAlerts: CalendarEntry[] = [];
  const reminders: CalendarEntry[] = [];
  const reviewDue: CalendarEntry[] = [];

  /* Past days carry no entries. The cell is still returned so the grid stays
     rectangular. */
  if (differenceInCalendarDays(date, today) >= 0) {
    for (const w of WINDOWS) {
      const dayIndex = differenceInCalendarDays(date, w.alertDate);
      if (dayIndex < 0 || dayIndex > w.totalDays) continue;
      const entry = entryFor(w, date, dayIndex);
      if (entry.entryType === "NEW_ALERT") newAlerts.push(entry);
      else if (entry.entryType === "REVIEW_DUE") reviewDue.push(entry);
      else reminders.push(entry);
    }
  }

  /* newAlerts and reviewDue by title; reminders by urgency, fewest days first. */
  newAlerts.sort(byTitle);
  reviewDue.sort(byTitle);
  reminders.sort((a, b) => a.daysRemaining - b.daysRemaining || byTitle(a, b));

  return {
    date: toApiDate(date),
    today: toApiDate(date) === toApiDate(today),
    inRange,
    newAlerts,
    reminders,
    reviewDue,
    newAlertCount: newAlerts.length,
    reminderCount: reminders.length,
    reviewDueCount: reviewDue.length,
    totalCount: newAlerts.length + reminders.length + reviewDue.length,
  };
}

/* Counts DOCUMENTS, not cells — a document inside a 10-day window is one
   active alert, not ten. */
function summaryFor(today: Date): CalendarSummary {
  const within = (d: Date, from: Date, to: Date) =>
    differenceInCalendarDays(d, from) >= 0 && differenceInCalendarDays(to, d) >= 0;

  return {
    allActive: WINDOWS.filter((w) => within(today, w.alertDate, w.reviewDate)).length,
    dueToday: WINDOWS.filter(
      (w) => differenceInCalendarDays(w.reviewDate, today) === 0,
    ).length,
    dueThisWeek: WINDOWS.filter((w) =>
      within(w.reviewDate, startOfWeek(today), endOfWeek(today)),
    ).length,
    dueThisMonth: WINDOWS.filter((w) =>
      within(w.reviewDate, startOfMonth(today), endOfMonth(today)),
    ).length,
  };
}

/* ── Month: POST /month/{year}/{month} ──────────────────────────────────── */

export function mockMonth(year: number, month: number, today: Date): CalendarPayload {
  const first = new Date(year, month - 1, 1);
  const start = startOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = addDays(start, i);
    const inRange =
      d.getMonth() === first.getMonth() && d.getFullYear() === first.getFullYear();
    return buildDay(d, inRange, today);
  });

  return {
    view: "MONTH",
    anchorDate: toApiDate(first),
    rangeStart: days[0].date,
    rangeEnd: days[days.length - 1].date,
    summary: summaryFor(today),
    days,
  };
}

/* ── Week: POST /week/{date} ────────────────────────────────────────────── */

export function mockWeek(date: string, today: Date): CalendarPayload {
  const start = startOfWeek(parseApiDate(date));
  const days = Array.from({ length: 7 }, (_, i) =>
    buildDay(addDays(start, i), true, today),
  );

  return {
    view: "WEEK",
    anchorDate: toApiDate(start),
    rangeStart: days[0].date,
    rangeEnd: days[days.length - 1].date,
    summary: summaryFor(today),
    days,
  };
}

/* ── Day: POST /day/{date} ──────────────────────────────────────────────── */

export function mockDay(date: string, today: Date): CalendarPayload {
  const day = buildDay(parseApiDate(date), true, today);

  return {
    view: "DAY",
    anchorDate: day.date,
    rangeStart: day.date,
    rangeEnd: day.date,
    summary: summaryFor(today),
    days: [day],
  };
}
